import { type ReactNode } from 'react';
import { Pagination, type PaginationMeta } from './Pagination';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  className?: string;
  render?: (row: T, index: number) => ReactNode;
}

interface SmartDataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string | number;
  meta?: PaginationMeta;
  loading?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  emptyMessage?: string;
  title?: string;
  actions?: ReactNode;
}

export function SmartDataTable<T>({
  columns,
  data,
  rowKey,
  meta,
  loading = false,
  searchPlaceholder = 'Rechercher…',
  searchValue = '',
  onSearchChange,
  onPageChange,
  onPerPageChange,
  emptyMessage = 'Aucune donnée',
  title,
  actions,
}: SmartDataTableProps<T>) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {(title || onSearchChange || actions) && (
        <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3">
          {title && <h3 className="text-sm font-bold text-slate-800">{title}</h3>}
          <div className="flex-1 flex items-center gap-2 sm:justify-end">
            {onSearchChange && (
              <div className="relative flex-1 sm:max-w-xs">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                />
              </div>
            )}
            {actions}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide ${col.className ?? ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-slate-400">
                  <i className="ri-loader-4-line text-2xl animate-spin block mb-2" />
                  Chargement…
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-slate-400">
                  <i className="ri-inbox-line text-2xl block mb-2" />
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={rowKey(row)} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-3 text-slate-700 ${col.className ?? ''}`}>
                      {col.render
                        ? col.render(row, idx)
                        : String((row as Record<string, unknown>)[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {meta && onPageChange && (
        <div className="px-4 pb-4">
          <Pagination
            meta={meta}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </div>
      )}
    </div>
  );
}
